<section class="dashboard">
  <header class="dashboard-head">
    <div>
      <span class="eyebrow">Live production command center</span>
      <h1>Live Production Overview</h1>
    </div>
    <div class="dashboard-actions">
      <mat-form-field appearance="outline" class="preset-field" subscriptSizing="dynamic">
        <mat-label>Date filter</mat-label>
        <mat-select [(ngModel)]="datePreset" (selectionChange)="applyPreset($event.value)">
          <mat-option value="today">Today</mat-option>
          <mat-option value="thisWeek">This Week</mat-option>
          <mat-option value="lastWeek">Last Week</mat-option>
          <mat-option value="thisMonth">This Month</mat-option>
          <mat-option value="lastMonth">Last Month</mat-option>
          <mat-option value="custom">Custom Range</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="range-field" subscriptSizing="dynamic" *ngIf="datePreset === 'custom'">
        <mat-label>Date range</mat-label>
        <mat-date-range-input [rangePicker]="picker">
          <input matStartDate placeholder="Start" [(ngModel)]="rangeStart" (dateChange)="onRangeStartChange()" />
          <input matEndDate placeholder="End" [(ngModel)]="rangeEnd" (dateChange)="applyDateRange()" />
        </mat-date-range-input>
        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-date-range-picker #picker (closed)="applyDateRange()"></mat-date-range-picker>
      </mat-form-field>

      <button class="refresh-btn" type="button" (click)="load()" [disabled]="loading">
        <mat-icon>refresh</mat-icon>
        <span>{{ loading ? 'Refreshing' : 'Refresh' }}</span>
      </button>
    </div>
  </header>

  <div class="metric-grid">
    <article class="metric panel" *ngFor="let card of cards" [class]="'metric panel tone-' + card.tone">
      <div class="metric-icon"><mat-icon>{{ card.icon }}</mat-icon></div>
      <span>{{ card.label }}</span>
      <strong>{{ summary?.[card.key] ?? 0 | number }}</strong>
    </article>
  </div>

  <div class="insight-grid">
    <article class="panel progress-panel">
      <div class="panel-head">
        <div>
          <h2>Assignment Completion</h2>
          <span>Completed against currently live assignments</span>
        </div>
        <strong>{{ completionRate() }}%</strong>
      </div>
      <div class="progress-track">
        <span [style.width.%]="completionRate()"></span>
      </div>
      <div class="split-stat">
        <span>Completed <b>{{ summary?.completedAssignments || 0 | number }}</b></span>
        <span>Live <b>{{ summary?.activeAssignments || 0 | number }}</b></span>
      </div>
    </article>

    <article class="panel progress-panel">
      <div class="panel-head">
        <div>
          <h2>Quality Yield</h2>
          <span>Good quantity vs rejected quantity</span>
        </div>
        <strong>{{ qualityRate() }}%</strong>
      </div>
      <div class="progress-track quality">
        <span [style.width.%]="qualityRate()"></span>
      </div>
      <div class="split-stat">
        <span>Produced <b>{{ summary?.productionTotals?.produced || 0 | number }}</b></span>
        <span>Rejected <b>{{ summary?.productionTotals?.rejected || 0 | number }}</b></span>
      </div>
    </article>
  </div>

  <div class="chart-grid">
    <article class="panel chart-panel wide">
      <div class="panel-head">
        <div>
          <h2>Production Trend</h2>
          <span>{{ rangeLabel() }} produced quantity with rejected share</span>
        </div>
      </div>
      <div class="bar-chart-scroll">
        <div class="bar-chart">
          <div class="bar-item" *ngFor="let row of trend(); trackBy: trackByDate">
            <div class="bar-shell" tabindex="0" [attr.aria-label]="formatDay(row.date) + ': produced ' + row.produced + ', rejected ' + row.rejected">
              <span class="bar-fill" [style.height.%]="barHeight(row)">
                <i [style.height.%]="rejectedHeight(row)"></i>
              </span>
              <div class="bar-tooltip">
                <strong>{{ formatDay(row.date) }}</strong>
                <span>Produced <b>{{ row.produced | number }}</b></span>
                <span>Rejected <b>{{ row.rejected | number }}</b></span>
                <span>Total <b>{{ row.produced + row.rejected | number }}</b></span>
                <small>{{ row.entries | number }} {{ row.entries === 1 ? 'entry' : 'entries' }}</small>
              </div>
            </div>
            <strong>{{ row.produced + row.rejected | number }}</strong>
            <small>{{ formatDay(row.date) }}</small>
          </div>
        </div>
      </div>
    </article>

    <article class="panel chart-panel">
      <div class="panel-head">
        <div>
          <h2>Assignment Status</h2>
          <span>{{ rangeLabel() }} work distribution by state</span>
        </div>
      </div>
      <div class="status-chart" *ngIf="chartData(summary?.filteredAssignmentStatus) as items">
        <div class="status-row" *ngFor="let item of items; trackBy: trackByLabel">
          <span class="legend-dot" [style.background]="item.color"></span>
          <span>{{ item.label }}</span>
          <div class="mini-track"><i [style.width.%]="percent(item.value, total(items))" [style.background]="item.color"></i></div>
          <strong>{{ item.value | number }}</strong>
        </div>
      </div>
    </article>

    <article class="panel chart-panel">
      <div class="panel-head">
        <div>
          <h2>Machine Health</h2>
          <span>Availability across machine register</span>
        </div>
      </div>
      <div class="status-chart" *ngIf="chartData(summary?.machineStatus) as items">
        <div class="status-row" *ngFor="let item of items; trackBy: trackByLabel">
          <span class="legend-dot" [style.background]="item.color"></span>
          <span>{{ item.label }}</span>
          <div class="mini-track"><i [style.width.%]="percent(item.value, total(items))" [style.background]="item.color"></i></div>
          <strong>{{ item.value | number }}</strong>
        </div>
      </div>
    </article>

    <article class="panel chart-panel">
      <div class="panel-head">
        <div>
          <h2>Worker Shifts</h2>
          <span>Active workforce split</span>
        </div>
      </div>
      <div class="pill-chart" *ngIf="chartData(summary?.workerShifts) as items">
        <div class="pill-row" *ngFor="let item of items; trackBy: trackByLabel">
          <span>{{ item.label }}</span>
          <strong>{{ item.value | number }}</strong>
          <div class="pill-track"><i [style.width.%]="percent(item.value, total(items))" [style.background]="item.color"></i></div>
        </div>
      </div>
    </article>

    <article class="panel chart-panel">
      <div class="panel-head">
        <div>
          <h2>Attendance Queue</h2>
          <span>{{ rangeLabel() }} review and terminal state mix</span>
        </div>
      </div>
      <div class="status-chart compact" *ngIf="chartData(summary?.filteredAttendanceStatus) as items">
        <div class="status-row" *ngFor="let item of items; trackBy: trackByLabel">
          <span class="legend-dot" [style.background]="item.color"></span>
          <span>{{ item.label }}</span>
          <strong>{{ item.value | number }}</strong>
        </div>
      </div>
    </article>

    <article class="panel chart-panel attendance-analytics wide">
      <div class="panel-head">
        <div>
          <h2>Attendance Summary</h2>
          <span>{{ rangeLabel() }} workforce availability and shift completion</span>
        </div>
      </div>

      <div class="analytics-grid">
        <div class="analytics-tile" *ngFor="let item of attendanceAnalytics(); trackBy: trackByLabel">
          <span class="analytics-icon" [style.background]="item.color"><mat-icon>{{ item.icon }}</mat-icon></span>
          <div>
            <small>{{ item.label }}</small>
            <strong>{{ item.value | number }}{{ item.suffix || '' }}</strong>
          </div>
        </div>
      </div>

      <div class="availability-chart" aria-label="Workforce availability chart">
        <div class="availability-row" *ngFor="let item of workforceAvailability(); trackBy: trackByLabel">
          <span>{{ item.label }}</span>
          <div class="availability-track">
            <i [style.width.%]="item.width" [style.background]="item.color"></i>
          </div>
          <strong>{{ item.value | number }}{{ item.suffix || '' }}</strong>
        </div>
      </div>
    </article>
  </div>

  <div class="activity-grid">
    <article class="panel table-panel">
      <div class="panel-head">
        <div>
          <h2>Recent Production Entries</h2>
          <span>Latest worker output updates</span>
        </div>
      </div>
      <div class="activity-list">
        <div class="activity-row" *ngFor="let row of summary?.recentProduction">
          <div>
            <strong>{{ row.worker?.name || 'Worker' }}</strong>
            <span>{{ row.assignment?.machine?.machineId || 'Machine' }} · {{ row.assignment?.operation?.operationName || 'Operation' }}</span>
          </div>
          <div class="qty">
            <b>{{ row.producedQuantity | number }}</b>
            <small>{{ row.rejectedQuantity | number }} rejected</small>
          </div>
        </div>
      </div>
    </article>

    <article class="panel table-panel">
      <div class="panel-head">
        <div>
          <h2>Recent Biometric Punches</h2>
          <span>Live from imports</span>
        </div>
      </div>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Device User</th>
              <th>Derived Type</th>
              <th>Record Time</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of summary?.recentPunches">
              <td>{{ displayCell(row.raw?.deviceUserId || row.employeeId) }}</td>
              <td>{{ displayCell(row.checkType) }}</td>
              <td>{{ formatDate(row.raw?.recordTime || row.time) }}</td>
              <td>
                <span class="status-badge" [ngClass]="statusClass(row.status)">
                  <mat-icon>{{ statusIcon(row.status) }}</mat-icon>
                  <span>{{ displayCell(row.status) }}</span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  </div>
</section>
